/**
 * Owner: Masa Bayu
 * File: demo-author-publish.ts
 * Date:Time: 2026-09-16 14:05
 * Description: Demo PublishRequest flow for T6-E2 author publications UI.
 */

import {
  applyExternalReviewDecision,
  attemptAuthorSelfApprove,
  createAuthorCapableAccount,
  createPublishRequest,
  createQxreadAuthorAccount,
  createQxreadSession,
  releasePublishRequestLive,
  submitPublishRequest,
  type QxreadAuthorAccount,
  type QxreadAccount,
  type QxreadPublishRequest,
  type QxreadSession,
} from '@qx/qxread-core';

const DEMO_ACCOUNT_ID = 'qxrdacc_demo_author';
const DEMO_NOW = '2026-09-16T06:05:00.000Z';

const ready = {
  hasTitle: true,
  hasCover: true,
  hasMetadata: true,
  qxpRefBound: true,
  hasVersionLabel: true,
} as const;

let demoAccount: QxreadAccount = createAuthorCapableAccount({
  accountId: DEMO_ACCOUNT_ID,
  displayName: 'Demo Author',
});
let demoSession: QxreadSession = createQxreadSession({
  state: 'authenticated',
  accountId: DEMO_ACCOUNT_ID,
  issuedAt: DEMO_NOW,
});
let demoAuthor: QxreadAuthorAccount = createQxreadAuthorAccount({
  accountId: DEMO_ACCOUNT_ID,
  displayName: 'Demo Author',
  slug: 'demo-author',
  createdAt: DEMO_NOW,
  updatedAt: DEMO_NOW,
});
let demoRequest: QxreadPublishRequest | null = null;
let lastStatus = 'No PublishRequest yet.';

function ensureActors(): void {
  demoAccount = createAuthorCapableAccount({
    accountId: DEMO_ACCOUNT_ID,
    displayName: 'Demo Author',
  });
  demoSession = createQxreadSession({
    state: 'authenticated',
    accountId: DEMO_ACCOUNT_ID,
    issuedAt: DEMO_NOW,
  });
  demoAuthor = createQxreadAuthorAccount({
    accountId: DEMO_ACCOUNT_ID,
    displayName: 'Demo Author',
    slug: 'demo-author',
    createdAt: DEMO_NOW,
    updatedAt: DEMO_NOW,
  });
}

export function resetDemoAuthorPublish(): void {
  ensureActors();
  demoRequest = null;
  lastStatus = 'No PublishRequest yet.';
}

export function getDemoPublishRequest(): QxreadPublishRequest | null {
  return demoRequest;
}

export function getDemoPublishStatus(): string {
  return lastStatus;
}

export function createDemoPublishRequest(): string {
  ensureActors();
  const result = createPublishRequest({
    session: demoSession,
    account: demoAccount,
    authorAccount: demoAuthor,
    publicationId: 'qxrdpub_demo_river',
    title: 'River Atlas',
    qxpPublicationRef: 'qxp_demo_river',
    versionLabel: '1.0.0',
    createdAt: DEMO_NOW,
  });
  if (!result.allowed) {
    lastStatus = result.reason;
    return lastStatus;
  }
  demoRequest = result.request;
  lastStatus = `Draft · ${result.request.title} · isQxwDocument=${String(result.request.isQxwDocument)}`;
  return lastStatus;
}

export function submitDemoPublishRequest(incomplete = false): string {
  if (demoRequest === null) {
    lastStatus = 'Create a PublishRequest first.';
    return lastStatus;
  }
  const result = submitPublishRequest({
    session: demoSession,
    account: demoAccount,
    authorAccount: demoAuthor,
    request: demoRequest,
    readiness: incomplete ? { ...ready, hasCover: false } : ready,
    submittedAt: DEMO_NOW,
  });
  demoRequest = result.request;
  lastStatus = result.allowed
    ? `In review · state=${result.request.state}`
    : `${result.reason}${result.readiness ? ` · missing ${result.readiness.missing.join(', ')}` : ''}`;
  return lastStatus;
}

export function attemptDemoSelfApprove(): string {
  if (demoRequest === null) {
    lastStatus = 'Create a PublishRequest first.';
    return lastStatus;
  }
  const result = attemptAuthorSelfApprove({
    session: demoSession,
    account: demoAccount,
    authorAccount: demoAuthor,
    request: demoRequest,
    attemptedAt: DEMO_NOW,
  });
  demoRequest = result.request;
  lastStatus = result.reason;
  return lastStatus;
}

export function applyDemoAdminApprove(): string {
  if (demoRequest === null || demoRequest.state !== 'in_review') {
    lastStatus = 'Submit for review before Admin decision.';
    return lastStatus;
  }
  demoRequest = applyExternalReviewDecision({
    actor: 'admin_ops',
    request: demoRequest,
    decision: 'approved',
    decidedAt: DEMO_NOW,
  });
  lastStatus = `Approved by admin_ops · state=${demoRequest.state}`;
  return lastStatus;
}

export function releaseDemoPublishLive(): string {
  if (demoRequest === null) {
    lastStatus = 'Create a PublishRequest first.';
    return lastStatus;
  }
  const result = releasePublishRequestLive({
    session: demoSession,
    account: demoAccount,
    authorAccount: demoAuthor,
    request: demoRequest,
    releasedAt: DEMO_NOW,
  });
  demoRequest = result.request;
  lastStatus = result.allowed
    ? `Live · mutatesQxp=${String(result.request.mutatesQxp)}`
    : result.reason;
  return lastStatus;
}
